import { create } from "zustand";
import { useRequestStore, GetAccessTokenFn } from "../BackendStore";
import { SubscriptionStatusResponse, ChargebeePortalSession } from "../../Contracts/Subscription";
import { isSubscriptionEnabled } from "../../Utils/FeatureFlags";

const subscriptionStatusUrl = "/v1/payments/teams/subscription-status/";
const chargebeePortalUrl = "/v1/payments/teams/chargebee-portal/";
const confirmSubscriptionUrl = "/v1/payments/teams/confirm-subscription/";

interface SubscriptionState {
  isLoading: boolean;
  subscriptionStatus: SubscriptionStatusResponse | null;
  portalSession: ChargebeePortalSession | null;
  getSubscriptionStatus: (getAccessTokenSilently: GetAccessTokenFn) => Promise<void>;
  getChargebeePortalSession: (getAccessTokenSilently: GetAccessTokenFn) => Promise<void>;
  confirmSubscription: (getAccessTokenSilently: GetAccessTokenFn) => Promise<void>;
  isPaymentsOnly: () => boolean;
  isSubscriptionRequired: () => boolean;
  isAccountDeactivated: () => boolean;
}

export const useSubscriptionStore = create<SubscriptionState>()((set, get) => {
  const { getRequest, postRequest } = useRequestStore.getState();

  const getSubscriptionStatus = async (getAccessTokenSilently: GetAccessTokenFn) => {
    if (!isSubscriptionEnabled) return;
    set({ isLoading: true });
    try {
      const response = await getRequest<SubscriptionStatusResponse>(
        getAccessTokenSilently,
        subscriptionStatusUrl
      );
      set({ subscriptionStatus: response, isLoading: false });
    } catch {
      console.error("Error fetching subscription status");
      set({ isLoading: false });
    }
  };

  const getChargebeePortalSession = async (getAccessTokenSilently: GetAccessTokenFn) => {
    set({ isLoading: true, portalSession: null });
    try {
      const response = await postRequest<{}, ChargebeePortalSession>(
        getAccessTokenSilently,
        chargebeePortalUrl,
        {}
      );
      set({ portalSession: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching Chargebee portal session");
      set({ isLoading: false });
      throw error;
    }
  };

  const confirmSubscription = async (getAccessTokenSilently: GetAccessTokenFn) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<{}, SubscriptionStatusResponse>(
        getAccessTokenSilently,
        confirmSubscriptionUrl,
        {}
      );
      set({ subscriptionStatus: response, isLoading: false });
    } catch (error) {
      console.error("Error confirming subscription");
      set({ isLoading: false });
      throw error;
    }
  };

  const isPaymentsOnly = () => {
    const sub = get().subscriptionStatus;
    return sub !== null && sub.status !== null;
  };

  const isSubscriptionRequired = () => {
    const sub = get().subscriptionStatus;
    return sub !== null && sub.status !== null && sub.status !== "Active";
  };

  const isAccountDeactivated = () => {
    const sub = get().subscriptionStatus;
    return sub !== null && (sub.status === "PastDue" || sub.status === "Cancelled");
  };

  return {
    isLoading: false,
    subscriptionStatus: null,
    portalSession: null,
    getSubscriptionStatus,
    getChargebeePortalSession,
    confirmSubscription,
    isPaymentsOnly,
    isSubscriptionRequired,
    isAccountDeactivated
  };
});
